/* eslint-disable */
import { useEffect, useState } from "react";
import AppShell from "../components/AppShell";
import api from "../lib/api";
import { toast, Toaster } from "sonner";

export default function RecModeDashboard() {
  const [listing, setListing] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [payouts, setPayouts] = useState(null);
  const [rates, setRates] = useState({ hourly: 0, daily: 0, weekly: 0, monthly: 0 });
  const [savingRates, setSavingRates] = useState(false);
  const [connecting, setConnecting] = useState(false);

  const load = async () => {
    try {
      const [l, b, p] = await Promise.all([
        api.get("/spaces/recmode"),
        api.get("/spaces/recmode/bookings"),
        api.get("/spaces/recmode/payouts"),
      ]);
      setListing(l.data); setRates(l.data.rates || {hourly:0,daily:0,weekly:0,monthly:0});
      setBookings(b.data); setPayouts(p.data);
    } catch { toast.error("Could not load dashboard"); }
  };
  useEffect(() => { load(); }, []);

  const saveRates = async (e) => {
    e.preventDefault();
    setSavingRates(true);
    try {
      const r = await api.put("/spaces/recmode/rates", {
        hourly: Number(rates.hourly), daily: Number(rates.daily),
        weekly: Number(rates.weekly || 0), monthly: Number(rates.monthly || 0),
      });
      setListing(r.data); toast.success("Rates updated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSavingRates(false); }
  };

  const connectStripe = async () => {
    setConnecting(true);
    try {
      const { data } = await api.post("/spaces/recmode/connect-stripe");
      if (data?.onboarding_url) {
        toast.success("Opening Stripe onboarding…");
        // Open Stripe's hosted onboarding in the same tab; Stripe will redirect
        // back to https://workmetier.app/profile when done.
        window.location.href = data.onboarding_url;
        return;
      }
      // Edge: backend returned without an onboarding URL, refresh state.
      await load();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Connect failed";
      toast.error(msg, { duration: 8000 });
    } finally {
      setConnecting(false);
    }
  };

  if (!listing) return <AppShell><div className="m-loading-inline">Loading…</div></AppShell>;

  return (
    <AppShell>
      <Toaster richColors position="top-center" />
      <div className="m-page-pad">
        <span className="m-eyebrow">Workspace dashboard</span>
        <h1 className="m-h1">{listing.name} <em>operations</em>.</h1>
        <p className="m-lede">Set rates, review bookings, and connect Stripe so every reservation lands in your account automatically.</p>

        <div className="rec-dash-stripe-banner" data-testid="rec-stripe-status">
          {listing.stripe_connected ? (
            <>
              <div>
                <span className="m-eyebrow small">Stripe Connect</span>
                <div className="rec-dash-stripe-line">Connected · <code>{listing.stripe_account_id}</code></div>
              </div>
              <button className="m-btn-ghost" onClick={connectStripe} data-testid="rec-reconnect-btn" disabled={connecting}>
                {connecting ? "…" : "Manage / Re-onboard"}
              </button>
            </>
          ) : (
            <>
              <div>
                <span className="m-eyebrow small">Stripe Connect</span>
                <div className="rec-dash-stripe-line">Not connected, payouts paused.</div>
              </div>
              <button className="m-btn-gold" onClick={connectStripe} disabled={connecting} data-testid="rec-connect-btn">
                {connecting ? "Connecting…" : "Connect Stripe"}
              </button>
            </>
          )}
        </div>

        {payouts && (
          <>
            <div className="m-stats-grid rec-dash-stats">
              <div className="m-stat" data-testid="rec-stat-bookings"><span className="m-stat-num">{bookings.length}</span><span className="m-stat-label">Bookings</span></div>
              <div className="m-stat" data-testid="rec-stat-gross"><span className="m-stat-num">${payouts.total_gross.toFixed(0)}</span><span className="m-stat-label">Gross booking value</span></div>
              <div className="m-stat" data-testid="rec-stat-fee"><span className="m-stat-num">${payouts.total_fees.toFixed(0)}</span><span className="m-stat-label">Métier fee</span></div>
              <div className="m-stat" data-testid="rec-stat-payout"><span className="m-stat-num">${payouts.total_payout.toFixed(0)}</span><span className="m-stat-label">Net payout</span></div>
            </div>
            <p className="m-muted small" style={{ marginTop: 10 }}>
              Your payout reflects the booking total minus applicable Métier fees, payment processing fees, refunds, and adjustments.
            </p>
          </>
        )}

        <div className="rec-dash-grid">
          <section className="m-card rec-dash-rates">
            <h3 className="m-side-title">Rate <em>card</em></h3>
            <form onSubmit={saveRates} className="m-form">
              <div className="m-form-grid-2">
                <label className="m-field"><span>Hourly $</span><input type="number" step="0.01" value={rates.hourly} onChange={e=>setRates({...rates, hourly:e.target.value})} required data-testid="rec-rate-hourly" /></label>
                <label className="m-field"><span>Daily $</span><input type="number" step="0.01" value={rates.daily} onChange={e=>setRates({...rates, daily:e.target.value})} required data-testid="rec-rate-daily" /></label>
              </div>
              <div className="m-form-grid-2">
                <label className="m-field"><span>Weekly $</span><input type="number" step="0.01" value={rates.weekly} onChange={e=>setRates({...rates, weekly:e.target.value})} data-testid="rec-rate-weekly" /></label>
                <label className="m-field"><span>Monthly $</span><input type="number" step="0.01" value={rates.monthly} onChange={e=>setRates({...rates, monthly:e.target.value})} data-testid="rec-rate-monthly" /></label>
              </div>
              <button type="submit" className="m-btn-gold" disabled={savingRates} data-testid="rec-rate-save">{savingRates?"Saving…":"Save rates"}</button>
              <p className="m-muted small">Hourly applies under 8h · Daily ≥ 8h · Weekly ≥ 7 days · Monthly ≥ 30 days.</p>
            </form>
          </section>

          <section className="m-card">
            <h3 className="m-side-title">Upcoming <em>bookings</em></h3>
            {bookings.length === 0 ? (
              <div className="m-empty small" data-testid="rec-dash-bookings">No bookings yet. Share <code>workmetier.com/spaces/recmode</code>.</div>
            ) : (
              <div className="m-bookings-list" data-testid="rec-dash-bookings">
                {bookings.map(b => (
                  <div key={b.booking_id} className="m-booking-row" data-testid={`rec-booking-${b.booking_id}`}>
                    <div>
                      <div className="m-booking-ws">{b.name}</div>
                      <div className="m-booking-meta">{b.email} · #{b.confirmation_number}</div>
                    </div>
                    <div className="m-booking-when">
                      <div className="m-booking-date">{b.date} · {b.start_time}</div>
                      <div className="m-booking-slot">{b.hours}h · ${b.amount.toFixed(2)} → ${b.host_payout.toFixed(2)} payout</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>

        {payouts && payouts.bookings.length > 0 && (
          <section className="m-card mt-20">
            <h3 className="m-side-title">Payout <em>history</em></h3>
            <div className="rec-payout-table" data-testid="rec-payout-history">
              <div className="rec-payout-head">
                <span>Date</span><span>Booking</span><span>Gross</span><span>Fee</span><span>Net payout</span>
              </div>
              {payouts.bookings.map(b => (
                <div key={b.booking_id} className="rec-payout-row" data-testid={`rec-payout-${b.booking_id}`}>
                  <span>{new Date(b.created_at).toLocaleDateString()}</span>
                  <span>#{b.confirmation_number}</span>
                  <span>${b.amount.toFixed(2)}</span>
                  <span>-${b.platform_fee.toFixed(2)}</span>
                  <span className="rec-payout-net">${b.host_payout.toFixed(2)}</span>
                </div>
              ))}
            </div>
            <p className="m-muted small">In production with Stripe Connect, these payouts settle to your bank within 2 business days automatically.</p>
          </section>
        )}
      </div>
    </AppShell>
  );
}
